/**
 * Leave Request Form — Digi Sahyadri WRMS
 * Supports Full Days, Half Days, and Fractional Leaves (e.g., 0.5, 1.5, 2.5 days)
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaCalendarAlt,
  FaPaperPlane,
  FaUserFriends,
  FaSun,
  FaMoon,
  FaMinus,
  FaPlus,
  FaClock,
} from 'react-icons/fa';
import WRMSFormHeader from '../shared/WRMSFormHeader';
import SignatureFooter from '../shared/SignatureFooter';
import { submitRequest } from '../../services/wrmsService';
import { playNotificationSound } from '../../../utils/soundService';
import { getEmployeesByDepartment } from '../../../services/firebase/employeeService';
import { getApprovalChain, getDeptGroup, getStageLabelForRole } from '../../utils/ApprovalRouter';

const LEAVE_TYPES = [
  { value: 'sick_leave', label: 'Sick Leave' },
  { value: 'emergency_leave', label: 'Emergency Leave' },
  { value: 'casual_leave', label: 'Casual Leave' },
  { value: 'paid_leave', label: 'Paid Leave' },
];

const today = () => new Date().toISOString().split('T')[0];

const calcDays = (from, to) => {
  if (!from || !to) return 0;
  const diff = (new Date(to) - new Date(from)) / (1000 * 60 * 60 * 24);
  return diff < 0 ? 0 : Math.round(diff) + 1;
};

const LeaveRequestForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [deptEmployees, setDeptEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Fetch department colleagues for the handover dropdown
  useEffect(() => {
    const dept = user?.department;
    if (!dept) return;
    setLoadingEmployees(true);
    getEmployeesByDepartment(dept)
      .then(res => {
        const list = (res.data || []).filter(emp => {
          // Exclude the logged-in user themselves
          const empEmail = emp.personal?.email || emp.email || '';
          return empEmail.toLowerCase() !== (user?.email || '').toLowerCase();
        });
        setDeptEmployees(list);
      })
      .catch(() => setDeptEmployees([]))
      .finally(() => setLoadingEmployees(false));
  }, [user?.department, user?.email]);

  const [form, setForm] = useState({
    applicationDate: today(),
    fromDate: '',
    toDate: '',
    daysCount: 0,
    durationType: 'full', // 'full' | 'half' | 'first_day_half' | 'last_day_half' | 'both_half' | 'custom'
    halfDaySession: 'first_half', // 'first_half' | 'second_half'
    durationLabel: 'Full Day',
    leaveType: '',
    reason: '',
    currentResponsibility: '',
    handoverTo: '',
  });

  const baseCalendarDays = calcDays(form.fromDate, form.toDate);

  // Auto-calculate daysCount and durationLabel based on dates and durationType
  useEffect(() => {
    if (!form.fromDate || !form.toDate) {
      setForm(prev => ({ ...prev, daysCount: 0, durationLabel: '' }));
      return;
    }

    const calendarSpan = calcDays(form.fromDate, form.toDate);
    if (calendarSpan <= 0) {
      setForm(prev => ({ ...prev, daysCount: 0, durationLabel: '' }));
      return;
    }

    if (calendarSpan === 1) {
      // Single day
      if (form.durationType === 'half') {
        const sessionTxt = form.halfDaySession === 'second_half' ? 'Second Half (Afternoon)' : 'First Half (Morning)';
        setForm(prev => ({
          ...prev,
          daysCount: 0.5,
          durationLabel: `Half Day (${sessionTxt})`,
        }));
      } else {
        setForm(prev => ({
          ...prev,
          daysCount: 1,
          durationType: 'full',
          durationLabel: 'Full Day (1 Day)',
        }));
      }
    } else {
      // Multi-day (2+ calendar days)
      if (form.durationType === 'first_day_half') {
        const count = calendarSpan - 0.5;
        setForm(prev => ({
          ...prev,
          daysCount: count,
          durationLabel: `${count} Days (First Day Half Day)`,
        }));
      } else if (form.durationType === 'last_day_half') {
        const count = calendarSpan - 0.5;
        setForm(prev => ({
          ...prev,
          daysCount: count,
          durationLabel: `${count} Days (Last Day Half Day)`,
        }));
      } else if (form.durationType === 'both_half') {
        const count = Math.max(0.5, calendarSpan - 1.0);
        setForm(prev => ({
          ...prev,
          daysCount: count,
          durationLabel: `${count} Day${count === 1 ? '' : 's'} (First & Last Day Half)`,
        }));
      } else if (form.durationType === 'custom') {
        // Keep valid custom daysCount bounded by [0.5, calendarSpan]
        const count = form.daysCount > 0 && form.daysCount <= calendarSpan ? form.daysCount : calendarSpan - 0.5;
        setForm(prev => ({
          ...prev,
          daysCount: count,
          durationLabel: `${count} Days (Custom Duration)`,
        }));
      } else {
        // Default to all full days
        setForm(prev => ({
          ...prev,
          daysCount: calendarSpan,
          durationType: 'full',
          durationLabel: `All Full Days (${calendarSpan} Days)`,
        }));
      }
    }
  }, [form.fromDate, form.toDate, form.durationType, form.halfDaySession]);

  const handleChange = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      // Auto-set toDate when fromDate is set and toDate is empty
      if (field === 'fromDate' && !prev.toDate) {
        next.toDate = value;
      }
      return next;
    });
  };

  const handleSelectDurationType = (type) => {
    setForm(prev => ({ ...prev, durationType: type }));
  };

  const handleSelectHalfDaySession = (session) => {
    setForm(prev => ({ ...prev, halfDaySession: session }));
  };

  const handleStepDays = (delta) => {
    if (!form.fromDate || !form.toDate || baseCalendarDays <= 0) return;
    const current = Number(form.daysCount) || 0;
    const nextVal = Math.round((current + delta) * 2) / 2; // round to nearest 0.5
    if (nextVal >= 0.5 && nextVal <= baseCalendarDays) {
      setForm(prev => ({
        ...prev,
        daysCount: nextVal,
        durationType: 'custom',
        durationLabel: `${nextVal} Day${nextVal === 1 ? '' : 's'} (Custom Adjustment)`,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fromDate || !form.toDate || !form.leaveType || !form.reason) {
      toast.error('Please fill in all required fields.');
      return;
    }
    if (new Date(form.toDate) < new Date(form.fromDate)) {
      toast.error('To Date cannot be before From Date.');
      return;
    }
    if (!form.daysCount || Number(form.daysCount) <= 0) {
      toast.error('Please select a valid leave duration.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        daysCount: Number(form.daysCount),
      };
      const result = await submitRequest('leave', payload, user);
      playNotificationSound();
      toast.success(`Leave request submitted! ID: ${result.requestId}`);
      navigate('/wrms');
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const deptGroup = getDeptGroup(user?.department, user?.role);
  const chain = getApprovalChain('leave', deptGroup);

  const signatories = chain.map(step => ({
    title: step.role === 'employee' ? 'Signature of Applicant' : `Signature of ${getStageLabelForRole(step.role)}`,
    name: step.role === 'employee' ? (user?.employeeName || user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '') : null,
    date: step.role === 'employee' ? new Date() : null,
  }));

  return (
    <div>
      <WRMSFormHeader formTitle="Leave Request Form" formSubtitle="Digi Sahyadri — Request System" />

      <div className="wrms-form-body">
        <form onSubmit={handleSubmit}>

          {/* Auto-populated Section */}
          <div className="wrms-section-label">Employee Details (Auto-populated)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div className="wrms-form-group">
              <label>Name</label>
              <input
                className="wrms-form-control wrms-field-readonly form-control"
                value={user?.employeeName || user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || ''}
                readOnly
              />
            </div>
            <div className="wrms-form-group">
              <label>Team ID</label>
              <input
                className="wrms-form-control wrms-field-readonly form-control"
                value={user?.employeeCode || user?.teamId || '—'}
                readOnly
              />
            </div>
            <div className="wrms-form-group">
              <label>Designation</label>
              <input
                className="wrms-form-control wrms-field-readonly form-control"
                value={user?.designation || '—'}
                readOnly
              />
            </div>
            <div className="wrms-form-group">
              <label>Department</label>
              <input
                className="wrms-form-control wrms-field-readonly form-control"
                value={user?.department || '—'}
                readOnly
              />
            </div>
          </div>

          {/* Application Dates */}
          <div className="wrms-section-label">Leave Details</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div className="wrms-form-group">
              <label>Date of Application <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                type="date"
                className="wrms-form-control form-control wrms-field-readonly"
                value={form.applicationDate}
                readOnly
              />
            </div>
            <div className="wrms-form-group">
              <label>From Date <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                type="date"
                className="wrms-form-control form-control"
                value={form.fromDate}
                min={today()}
                onChange={e => handleChange('fromDate', e.target.value)}
                required
              />
            </div>
            <div className="wrms-form-group">
              <label>To Date <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                type="date"
                className="wrms-form-control form-control"
                value={form.toDate}
                min={form.fromDate || today()}
                onChange={e => handleChange('toDate', e.target.value)}
                required
              />
            </div>
          </div>

          {/* Days & Duration Options */}
          <div style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: '12px', border: '1.5px solid #e2e8f0', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
              <div>
                <label style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '2px', display: 'block' }}>
                  Leave Duration & Day Count <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <div style={{ fontSize: '0.76rem', color: '#64748b' }}>
                  {baseCalendarDays > 1 
                    ? `Span of ${baseCalendarDays} calendar days. Select full days or 0.5-day half leave options below.`
                    : baseCalendarDays === 1
                    ? 'Single day leave. Choose Full Day (1.0) or Half Day (0.5).'
                    : 'Select From Date and To Date above.'}
                </div>
              </div>

              {/* Live Day Badge + Stepper */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className="wrms-days-badge" style={{ fontSize: '0.95rem', padding: '8px 16px' }}>
                  <FaCalendarAlt size={14} />
                  <span>{form.daysCount} {form.daysCount === 1 ? 'day' : 'days'}</span>
                </div>

                {baseCalendarDays > 0 && (
                  <div className="wrms-stepper-wrap" title="Adjust leave in 0.5 day increments">
                    <button
                      type="button"
                      className="wrms-stepper-btn"
                      onClick={() => handleStepDays(-0.5)}
                      disabled={form.daysCount <= 0.5}
                      title="Decrease by 0.5 day"
                    >
                      <FaMinus size={10} />
                    </button>
                    <span className="wrms-stepper-display">{form.daysCount} d</span>
                    <button
                      type="button"
                      className="wrms-stepper-btn"
                      onClick={() => handleStepDays(0.5)}
                      disabled={form.daysCount >= baseCalendarDays}
                      title="Increase by 0.5 day"
                    >
                      <FaPlus size={10} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Single Day Options */}
            {baseCalendarDays === 1 && (
              <div>
                <div className="wrms-duration-toggle" style={{ marginBottom: form.durationType === 'half' ? '14px' : '0' }}>
                  <div
                    className={`wrms-duration-option ${form.durationType === 'full' ? 'active' : ''}`}
                    onClick={() => handleSelectDurationType('full')}
                  >
                    Full Day (1.0 Day)
                  </div>
                  <div
                    className={`wrms-duration-option ${form.durationType === 'half' ? 'active' : ''}`}
                    onClick={() => handleSelectDurationType('half')}
                  >
                    Half Day (0.5 Day)
                  </div>
                </div>

                {/* Session Selector for Half Day */}
                {form.durationType === 'half' && (
                  <div style={{ marginTop: '12px', background: '#ffffff', padding: '12px 14px', borderRadius: '10px', border: '1px solid #c7d2fe' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#3730a3', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FaClock size={12} /> Select Half-Day Session:
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className={`wrms-btn-outline ${form.halfDaySession === 'first_half' ? 'active' : ''}`}
                        onClick={() => handleSelectHalfDaySession('first_half')}
                        style={{
                          fontSize: '0.82rem',
                          padding: '6px 14px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          background: form.halfDaySession === 'first_half' ? '#4f46e5' : '#ffffff',
                          color: form.halfDaySession === 'first_half' ? '#ffffff' : '#334155',
                          borderColor: form.halfDaySession === 'first_half' ? '#4f46e5' : '#cbd5e1',
                          borderRadius: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        <FaSun size={12} style={{ color: form.halfDaySession === 'first_half' ? '#fef08a' : '#f59e0b' }} />
                        First Half (Morning)
                      </button>
                      <button
                        type="button"
                        className={`wrms-btn-outline ${form.halfDaySession === 'second_half' ? 'active' : ''}`}
                        onClick={() => handleSelectHalfDaySession('second_half')}
                        style={{
                          fontSize: '0.82rem',
                          padding: '6px 14px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          background: form.halfDaySession === 'second_half' ? '#4f46e5' : '#ffffff',
                          color: form.halfDaySession === 'second_half' ? '#ffffff' : '#334155',
                          borderColor: form.halfDaySession === 'second_half' ? '#4f46e5' : '#cbd5e1',
                          borderRadius: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        <FaMoon size={12} style={{ color: form.halfDaySession === 'second_half' ? '#e0e7ff' : '#6366f1' }} />
                        Second Half (Afternoon)
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Multi-Day Preset Pills (e.g. 1.5 days, 2.5 days, etc.) */}
            {baseCalendarDays > 1 && (
              <div>
                <div className="wrms-duration-grid">
                  {/* Full Days */}
                  <div
                    className={`wrms-duration-pill ${form.durationType === 'full' ? 'active' : ''}`}
                    onClick={() => handleSelectDurationType('full')}
                  >
                    <span className="wrms-duration-pill-title">
                      {baseCalendarDays}.0 Days
                    </span>
                    <span className="wrms-duration-pill-sub">All Full Days</span>
                  </div>

                  {/* First Day Half Day -> e.g. 1.5 or 2.5 days */}
                  <div
                    className={`wrms-duration-pill ${form.durationType === 'first_day_half' ? 'active' : ''}`}
                    onClick={() => handleSelectDurationType('first_day_half')}
                  >
                    <span className="wrms-duration-pill-title">
                      {baseCalendarDays - 0.5} Days
                    </span>
                    <span className="wrms-duration-pill-sub">First Day Half Day</span>
                  </div>

                  {/* Last Day Half Day -> e.g. 1.5 or 2.5 days */}
                  <div
                    className={`wrms-duration-pill ${form.durationType === 'last_day_half' ? 'active' : ''}`}
                    onClick={() => handleSelectDurationType('last_day_half')}
                  >
                    <span className="wrms-duration-pill-title">
                      {baseCalendarDays - 0.5} Days
                    </span>
                    <span className="wrms-duration-pill-sub">Last Day Half Day</span>
                  </div>

                  {/* Both First & Last Half */}
                  <div
                    className={`wrms-duration-pill ${form.durationType === 'both_half' ? 'active' : ''}`}
                    onClick={() => handleSelectDurationType('both_half')}
                  >
                    <span className="wrms-duration-pill-title">
                      {Math.max(0.5, baseCalendarDays - 1)} Day{Math.max(0.5, baseCalendarDays - 1) === 1 ? '' : 's'}
                    </span>
                    <span className="wrms-duration-pill-sub">First & Last Half</span>
                  </div>
                </div>
              </div>
            )}

            {/* Live Summary Alert */}
            {baseCalendarDays > 0 && (
              <div className="wrms-leave-summary-card">
                <div className="wrms-leave-summary-text">
                  <div className="wrms-leave-summary-heading">
                    Selected Duration: <strong>{form.daysCount} {form.daysCount === 1 ? 'Day' : 'Days'} Leave</strong>
                  </div>
                  <div className="wrms-leave-summary-sub">
                    {form.durationLabel || `${form.daysCount} days`} • ({form.fromDate} to {form.toDate})
                  </div>
                </div>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#4f46e5' }}>
                  {form.daysCount % 1 !== 0 ? 'Fractional Leave' : 'Full Days'}
                </div>
              </div>
            )}
          </div>

          {/* Leave Type */}
          <div className="wrms-form-group" style={{ marginBottom: '24px' }}>
            <label>Leave Type <span style={{ color: '#dc2626' }}>*</span></label>
            <div className="wrms-radio-group" style={{ marginTop: '8px' }}>
              {LEAVE_TYPES.map(lt => (
                <label
                  key={lt.value}
                  className={`wrms-radio-btn ${form.leaveType === lt.value ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="leaveType"
                    value={lt.value}
                    checked={form.leaveType === lt.value}
                    onChange={e => handleChange('leaveType', e.target.value)}
                    style={{ display: 'none' }}
                  />
                  {lt.label}
                </label>
              ))}
            </div>
          </div>

          {/* Reason */}
          <div className="wrms-form-group" style={{ marginBottom: '20px' }}>
            <label>Reason for Leave <span style={{ color: '#dc2626' }}>*</span></label>
            <textarea
              className="wrms-form-control form-control"
              rows={3}
              placeholder="Please describe the reason for your leave..."
              value={form.reason}
              onChange={e => handleChange('reason', e.target.value)}
              required
            />
          </div>

          {/* Responsibility */}
          <div className="wrms-section-label">Handover Details</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div className="wrms-form-group">
              <label>Current Responsibility — Priority-wise</label>
              <textarea
                className="wrms-form-control form-control"
                rows={3}
                placeholder="List your current responsibilities in priority order..."
                value={form.currentResponsibility}
                onChange={e => handleChange('currentResponsibility', e.target.value)}
              />
            </div>
            <div className="wrms-form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FaUserFriends size={13} style={{ color: '#4f46e5' }} />
                Current Responsibility Hand Over To
              </label>
              {loadingEmployees ? (
                <div style={{ padding: '10px', fontSize: '0.82rem', color: '#64748b' }}>Loading department employees...</div>
              ) : deptEmployees.length > 0 ? (
                <select
                  className="wrms-form-control form-control"
                  value={form.handoverToId || form.handoverTo}
                  onChange={e => {
                    const val = e.target.value;
                    const selectedEmp = deptEmployees.find(emp => emp.id === val || (emp.personal?.fullName || emp.firstName) === val);
                    if (selectedEmp) {
                      const name = selectedEmp.personal?.fullName ||
                        `${selectedEmp.personal?.firstName || ''} ${selectedEmp.personal?.lastName || ''}`.trim() ||
                        selectedEmp.firstName || 'Unknown';
                      setForm(prev => ({
                        ...prev,
                        handoverTo: name,
                        handoverToId: selectedEmp.id,
                        handoverToEmail: selectedEmp.personal?.email || selectedEmp.email || '',
                        handoverToTeamId: selectedEmp.job?.employeeId || selectedEmp.employeeCode || selectedEmp.teamId || '',
                      }));
                    } else {
                      setForm(prev => ({ ...prev, handoverTo: val, handoverToId: '', handoverToEmail: '', handoverToTeamId: '' }));
                    }
                  }}
                  style={{ height: '42px' }}
                >
                  <option value="">— Select employee —</option>
                  {deptEmployees.map(emp => {
                    const name = emp.personal?.fullName ||
                      `${emp.personal?.firstName || ''} ${emp.personal?.lastName || ''}`.trim() ||
                      emp.firstName || 'Unknown';
                    const designation = emp.job?.designation || emp.designation || '';
                    return (
                      <option key={emp.id} value={emp.id}>
                        {name}{designation ? ` — ${designation}` : ''}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <input
                  type="text"
                  className="wrms-form-control form-control"
                  placeholder="Enter employee name (no dept colleagues found)"
                  value={form.handoverTo}
                  onChange={e => handleChange('handoverTo', e.target.value)}
                />
              )}
              {deptEmployees.length > 0 && (
                <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '4px' }}>
                  Showing {deptEmployees.length} active colleague{deptEmployees.length !== 1 ? 's' : ''} from your department · Selected colleague will receive handover request for approval first
                </div>
              )}
            </div>
          </div>

          {/* Application Approved On */}
          <div className="wrms-form-group" style={{ marginBottom: '28px' }}>
            <label>Application Approved On (System auto-fills on HR approval)</label>
            <input
              className="wrms-form-control wrms-field-readonly form-control"
              value="Pending HR Final Approval"
              readOnly
            />
          </div>

          {/* Signature Footer */}
          <SignatureFooter signatories={signatories} />

          {/* Submit */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
            <button
              type="button"
              className="wrms-btn-outline"
              onClick={() => navigate('/wrms')}
              style={{ padding: '10px 24px' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="wrms-btn-primary"
              disabled={submitting}
            >
              <FaPaperPlane size={13} />
              {submitting ? 'Submitting...' : 'Submit Leave Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeaveRequestForm;
